import Menu from './menu';

import log from '../lib/log';
import Util from '../utils/util';
import { isMobile } from '../utils/detect';
import { onSecondaryPress } from '../utils/press';

import { Modules, Opcodes } from '@kaetram/common/network';

import type Inventory from './inventory';
import type { StorePacket } from '@kaetram/common/types/messages/outgoing';
import type { SerializedStoreItem } from '@kaetram/common/types/stores';

type SelectCallback = (opcode: Opcodes.Store, key: string, index: number, count?: number) => void;

const CATALOG_KEY = 'universalshop',
    CATEGORY_ORDER = [
        'Armas',
        'Armaduras',
        'Botas',
        'Amuletos',
        'Anillos',
        'Escudos',
        'Flechas',
        'Alas',
        'Skins de Arma',
        'Skins de Armadura',
        'Objetos',
        'Otros'
    ];

export default class Store extends Menu {
    private key = '';
    private currency = 'gold';
    private currencyName = 'Oro';

    private selectedIndex = -1;
    private selectedCount = -1;

    private selectedBuyIndex = -1;

    private storeContainer: HTMLElement = document.querySelector('#store-container')!;

    private storeHelp: HTMLElement = document.querySelector('#store-help')!;

    private confirmSell: HTMLElement = document.querySelector('#confirm-sell')!;

    private sellSlot: HTMLElement = document.querySelector('#store-sell-slot')!;
    private sellSlotText: HTMLElement = document.querySelector('#store-sell-slot-text')!;
    private sellSlotReturn: HTMLElement = document.querySelector('#store-sell-slot-return')!;
    private sellSlotReturnText: HTMLElement = document.querySelector(
        '#store-sell-slot-return-text'
    )!;

    private storeList: HTMLUListElement = document.querySelector('#store-container')!;
    private inventoryList: HTMLUListElement = document.querySelector('#store-inventory-slots')!;

    private storeBalance: HTMLElement = document.querySelector('#store-balance')!;

    public buyDialog: HTMLElement = document.querySelector('#store-buy')!;
    private buyCount: HTMLInputElement = document.querySelector('#store-buy .dialog-count')!;
    private buyAccept: HTMLElement = document.querySelector('#store-buy .dialog-accept')!;
    private buyCancel: HTMLElement = document.querySelector('#store-buy .dialog-cancel')!;

    // Catalog mode elements (may be absent if HTML template is out of date)
    private catalogOverlay = document.querySelector<HTMLElement>('#catalog-store');
    private catalogCategories = document.querySelector<HTMLElement>('#catalog-categories');
    private catalogItems = document.querySelector<HTMLElement>('#catalog-items');
    private catalogTitle = document.querySelector<HTMLElement>('#catalog-title');
    private catalogClose = document.querySelector<HTMLElement>('#catalog-close');
    private catalogBalance = document.querySelector<HTMLElement>('#catalog-balance');
    private catalogBuyDialog = document.querySelector<HTMLElement>('#catalog-buy-dialog');
    private catalogBuyCount = document.querySelector<HTMLInputElement>(
        '#catalog-buy-dialog .dialog-count'
    );
    private catalogBuyAccept = document.querySelector<HTMLElement>(
        '#catalog-buy-dialog .dialog-accept'
    );
    private catalogBuyCancel = document.querySelector<HTMLElement>(
        '#catalog-buy-dialog .dialog-cancel'
    );

    private isCatalog = false;
    private allItems: SerializedStoreItem[] = [];
    private activeCategory = '';

    private selectCallback?: SelectCallback;

    public constructor(private inventory: Inventory) {
        super('#store', '#close-store');

        this.resize();

        this.sellSlot.addEventListener('click', this.synchronize.bind(this));
        this.confirmSell.addEventListener('click', this.sell.bind(this));

        this.buyCancel.addEventListener('click', this.hideBuyDialog.bind(this));
        this.buyAccept.addEventListener('click', this.handleBuy.bind(this));

        this.catalogClose?.addEventListener('click', () => this.hide());
        this.catalogBuyCancel?.addEventListener('click', () => this.hideCatalogBuyDialog());
        this.catalogBuyAccept?.addEventListener('click', () => this.handleCatalogBuy());

        for (let i = 0; i < Modules.Constants.INVENTORY_SIZE; i++)
            this.inventoryList.append(
                Util.createSlot(Modules.ContainerType.Inventory, i, this.select.bind(this))
            );
    }

    public override resize(): void {
        let action = isMobile() ? 'Mantén presionado' : 'Clic derecho';

        this.storeHelp.textContent = `${action} para comprar varios objetos.`;
    }

    public update(info: StorePacket): void {
        this.clear();

        this.key = info.key!;
        this.currency = info.currency!;
        this.currencyName = info.currencyName || info.currency!;

        if (this.key === CATALOG_KEY && this.catalogOverlay) {
            this.isCatalog = true;
            this.allItems = info.items!;
            this.renderCatalog();
            return;
        }

        this.isCatalog = false;

        for (let i = 0; i < info.items!.length; i++)
            this.storeList.append(this.createStoreItem(info.items![i], i));
    }

    public select(_type: Modules.ContainerType, index: number): void {
        this.selectCallback?.(Opcodes.Store.Select, this.key, index);
    }

    private sell(): void {
        this.selectCallback?.(Opcodes.Store.Sell, this.key, this.selectedIndex, this.selectedCount);
    }

    private buy(index: number, count = 1): void {
        this.selectCallback?.(Opcodes.Store.Buy, this.key, index, count);
    }

    private handleBuy(): void {
        this.buy(this.selectedBuyIndex, this.buyCount.valueAsNumber);

        this.hideBuyDialog();
    }

    public showBuyDialog(): void {
        Util.fadeIn(this.buyDialog);

        this.storeContainer.classList.add('dimmed');

        this.buyCount.value = '1';
        this.buyCount.focus();
    }

    private hideBuyDialog(): void {
        Util.fadeOut(this.buyDialog);

        this.storeContainer.classList.remove('dimmed');
    }

    public override synchronize(): void {
        if (!this.isVisible()) return;

        this.clearSellSlot();

        this.inventory.forEachSlot((index: number, slot: HTMLElement) => {
            let image = this.getElement(index).querySelector<HTMLElement>('.item-image')!,
                count = this.getElement(index).querySelector<HTMLElement>('.item-count')!,
                slotImage = slot.querySelector<HTMLElement>('.item-image')!;

            if (!slotImage) return;

            image.style.backgroundImage = slotImage.style.backgroundImage;
            count.textContent = slot.textContent;
        });

        this.updateBalance();
    }

    public override show(info: StorePacket): void {
        this.update(info);

        if (this.isCatalog && this.catalogOverlay) {
            this.catalogOverlay.style.display = 'flex';
            this.updateBalance();
            return;
        }

        super.show();
        this.synchronize();
    }

    public override hide(): void {
        if (this.isCatalog && this.catalogOverlay) {
            this.catalogOverlay.style.display = 'none';
            this.isCatalog = false;
            this.allItems = [];
            this.activeCategory = '';
            return;
        }

        super.hide();
    }

    public override isVisible(): boolean {
        if (this.isCatalog && this.catalogOverlay)
            return this.catalogOverlay.style.display === 'flex';
        return super.isVisible();
    }

    private clear(): void {
        this.key = '';
        this.currency = 'gold';
        this.currencyName = 'Oro';

        this.storeList.innerHTML = '';
    }

    private clearSellSlot(): void {
        this.selectedIndex = -1;
        this.selectedCount = -1;

        this.sellSlot.style.backgroundImage = '';
        this.sellSlotText.textContent = '';
        this.sellSlotReturn.style.backgroundImage = '';
        this.sellSlotReturnText.textContent = '';
    }

    /**
     * Counts how many units of the current currency the player has in inventory.
     */

    private getCurrencyCount(): number {
        let total = 0;

        this.inventory.forEachSlot((_index: number, slot: HTMLElement) => {
            if (slot?.dataset?.key === this.currency) {
                let count = parseInt(slot.dataset.count || '0', 10);

                if (count > 0) total += count;
            }
        });

        return total;
    }

    /**
     * Updates the balance display in both the normal store and catalog.
     */

    private updateBalance(): void {
        let count = this.getCurrencyCount(),
            text = `${count.toLocaleString()} ${this.currencyName}`;

        if (this.storeBalance) {
            this.storeBalance.innerHTML = '';

            let icon = document.createElement('span');
            icon.className = 'store-balance-icon';
            icon.style.backgroundImage = Util.getImageURL(this.currency);

            let label = document.createElement('span');
            label.textContent = text;

            this.storeBalance.append(icon, label);
        }

        if (this.isCatalog && this.catalogBalance) {
            this.catalogBalance.innerHTML = '';

            let icon = document.createElement('span');
            icon.className = 'store-currency-icon';
            icon.style.backgroundImage = Util.getImageURL(this.currency);

            let label = document.createElement('span');
            label.textContent = text;

            this.catalogBalance.append(icon, label);
        }
    }

    public move(info: StorePacket): void {
        if (info.key !== this.key) return log.error(`Invalid store key provided for the select.`);

        this.synchronize();

        let image = this.getElement(info.item!.index!).querySelector<HTMLElement>('.item-image')!,
            count = this.getElement(info.item!.index!).querySelector<HTMLElement>('.item-count')!;

        if (!image || !count) return log.error(`[Store] Could not find image and count elements.`);

        this.sellSlot.style.backgroundImage = image.style.backgroundImage;
        this.sellSlotText.textContent = count.textContent;
        this.sellSlotReturn.style.backgroundImage = Util.getImageURL(this.currency);
        this.sellSlotReturnText.textContent = info.item!.price.toString() || '';

        image.style.backgroundImage = '';
        count.textContent = '';

        this.selectedIndex = info.item!.index!;
        this.selectedCount = info.item!.count!;
    }

    // ─── Catalog Mode ───

    private renderCatalog(): void {
        if (!this.catalogCategories || !this.catalogItems) return;

        this.catalogCategories.innerHTML = '';
        this.catalogItems.innerHTML = '';

        let categories = new Set<string>();

        for (let item of this.allItems) if (item.category) categories.add(item.category);

        let sorted = CATEGORY_ORDER.filter((c) => categories.has(c));

        for (let cat of sorted) {
            let count = this.allItems.filter((i) => i.category === cat).length,
                btn = document.createElement('div');

            btn.className = 'catalog-cat-btn';
            btn.textContent = `${cat} (${count})`;
            btn.addEventListener('click', () => this.selectCategory(cat));

            this.catalogCategories.append(btn);
        }

        if (sorted.length > 0) this.selectCategory(sorted[0]);
    }

    private selectCategory(category: string): void {
        if (!this.catalogCategories || !this.catalogItems || !this.catalogTitle) return;

        this.activeCategory = category;
        this.catalogTitle.textContent = category;

        let buttons = this.catalogCategories.querySelectorAll('.catalog-cat-btn');

        for (let btn of buttons)
            btn.classList.toggle('active', btn.textContent?.startsWith(category) || false);

        this.catalogItems.innerHTML = '';

        for (let i = 0; i < this.allItems.length; i++) {
            let item = this.allItems[i];

            if (item.category !== category) continue;

            this.catalogItems.append(this.createCatalogItem(item, i));
        }
    }

    private createCatalogItem(item: SerializedStoreItem, index: number): HTMLElement {
        let el = document.createElement('div');

        el.className = 'catalog-item';

        let img = document.createElement('div');

        img.className = 'catalog-item-img';
        img.style.backgroundImage = Util.getImageURL(item.key);

        let info = document.createElement('div');

        info.className = 'catalog-item-info';

        let name = document.createElement('div');

        name.className = 'catalog-item-name stroke';
        name.textContent = item.name;

        let price = document.createElement('div');

        price.className = 'catalog-item-price stroke';

        let priceIcon = document.createElement('span');
        priceIcon.className = 'store-currency-icon-sm';
        priceIcon.style.backgroundImage = Util.getImageURL(this.currency);

        let priceText = document.createElement('span');
        priceText.textContent = `${item.price.toLocaleString()}`;

        price.append(priceIcon, priceText);

        if (item.count === -1) info.append(name, price);
        else {
            let stock = document.createElement('div');
            stock.className = 'catalog-item-stock stroke';
            stock.textContent = `Disponible: ${item.count}`;
            info.append(name, price, stock);
        }

        el.append(img, info);

        el.addEventListener('click', () => this.buy(index));
        onSecondaryPress(el, () => {
            this.selectedBuyIndex = index;
            this.showCatalogBuyDialog();
        });

        return el;
    }

    private showCatalogBuyDialog(): void {
        if (!this.catalogBuyDialog || !this.catalogBuyCount) return;
        this.catalogBuyDialog.style.display = 'flex';
        this.catalogBuyCount.value = '1';
        this.catalogBuyCount.focus();
    }

    private hideCatalogBuyDialog(): void {
        if (this.catalogBuyDialog) this.catalogBuyDialog.style.display = 'none';
    }

    private handleCatalogBuy(): void {
        if (!this.catalogBuyCount) return;
        this.buy(this.selectedBuyIndex, this.catalogBuyCount.valueAsNumber);
        this.hideCatalogBuyDialog();
    }

    // ─── Normal Store Helpers ───

    private createStoreItem(item: SerializedStoreItem, index: number): HTMLElement {
        let listElement = document.createElement('li'),
            image = document.createElement('div'),
            name = document.createElement('div'),
            count = document.createElement('div'),
            price = document.createElement('div');

        listElement.classList.add('store-item');
        image.classList.add('store-item-image');
        name.classList.add('store-item-name', 'stroke');
        count.classList.add('store-item-count', 'stroke');
        price.classList.add('store-item-price', 'stroke');

        if (item.count !== -1) count.textContent = `x${item.count.toString()}`;
        name.textContent = item.name;

        let priceIcon = document.createElement('span');
        priceIcon.className = 'store-currency-icon-sm';
        priceIcon.style.backgroundImage = Util.getImageURL(this.currency);

        let priceText = document.createElement('span');
        priceText.textContent = `${item.price.toLocaleString()}`;

        price.append(priceIcon, priceText);

        image.style.backgroundImage = Util.getImageURL(item.key);

        listElement.addEventListener('click', () => this.buy(index));
        onSecondaryPress(listElement, () => {
            this.selectedBuyIndex = index;

            this.showBuyDialog();
        });

        listElement.append(image, name, count, price);

        return listElement;
    }

    private getElement(index: number): HTMLElement {
        return this.inventoryList.children[index].querySelector('div') as HTMLElement;
    }

    public onSelect(callback: SelectCallback): void {
        this.selectCallback = callback;
    }
}
